/**
 * ICAGenerator.jsx — Independent Contractor Agreement Generator
 *
 * Full 15-section ICA template for Roto Flight LLC media production contractors.
 * Form fields: contractor name, effective date, compensation terms, optional toggles.
 * Generates: clean HTML printable page + logs to legal_docs table.
 * Governing law: State of Wyoming.
 */

import { useState, useEffect } from 'react';
import { useCreateLegalDoc } from '../../hooks/useLegal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../layout/Toast';
import { uid, fmtD, toLocalDateStr } from '../../lib/utils';
import { useRoster } from '../../hooks/useRoster';

const today = toLocalDateStr(new Date());

export default function ICAGenerator() {
  const createLegalDoc = useCreateLegalDoc();
  const { profile } = useAuth();
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const { data: roster = [] } = useRoster();

  const [formData, setFormData] = useState({
    fullName: '',
    authorizedRep: profile?.full_name || profile?.email || '',
    date: today,
    compensationTerms: '',
    includeDroneOps: false,
    includeClientProtection: true,
    includeKillFee: true,
    killFeePercent: '50',
    killFeeHours: '24',
    nonSolicitMonths: '12',
    paymentDays: '14',
  });

  useEffect(() => {
    if (profile && !formData.authorizedRep) {
      setFormData(prev => ({ ...prev, authorizedRep: profile.full_name || profile.email || '' }));
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const esc = (str) => str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const generatePrintableHTML = () => {
    const {
      fullName,
      date,
      compensationTerms,
      includeDroneOps,
      includeClientProtection,
      includeKillFee,
      killFeePercent,
      killFeeHours,
      nonSolicitMonths,
      paymentDays,
    } = formData;

    const formattedDate = fmtD(date);
    const safeName = esc(fullName);
    const safeComp = esc(compensationTerms);

    // Build drone section (§9)
    const droneSection = includeDroneOps
      ? `<p>If Contractor participates in drone operations, Contractor must hold a valid FAA Part 107 Remote Pilot Certificate if operating the drone and comply with all FAA regulations and airspace restrictions. Unless otherwise agreed, Roto Flight LLC will act as the responsible drone operator and maintain operational control.</p>`
      : '';

    // Build client protection section (§8)
    const clientProtectionSection = includeClientProtection
      ? `
      <h3>8. Client Protection</h3>
      <p>During the term of this Agreement and for ${esc(nonSolicitMonths)} months following the last project, Contractor agrees not to directly solicit, contact, or contract with clients introduced by the Company for competing production services without the Company&rsquo;s prior written consent. This clause does not prevent Contractor from working independently with unrelated clients.</p>
      `
      : '';

    // Build kill fee section (§10)
    const killFeeSection = includeKillFee
      ? `
      <h3>10. Kill Fee / Cancellation</h3>
      <p>If a confirmed shoot is canceled by the Company within ${esc(killFeeHours)} hours of the scheduled start time, Contractor may be entitled to a kill fee equal to ${esc(killFeePercent)}% of the agreed day rate. No kill fee applies for cancellations with more than ${esc(killFeeHours)} hours&rsquo; notice. If cancellation occurs due to weather, safety concerns, or circumstances outside the Company&rsquo;s control (force majeure), the Company may reschedule without penalty.</p>
      `
      : '';

    // Re-number sections based on toggles
    let sectionNum = 8;
    const s8 = includeClientProtection ? sectionNum++ : null;
    const s9label = sectionNum++;
    const s10 = includeKillFee ? sectionNum++ : null;
    const s11 = sectionNum++;
    const s12 = sectionNum++;
    const s13 = sectionNum++;
    const s14 = sectionNum++;
    const s15 = sectionNum++;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Independent Contractor Agreement &mdash; ${safeName}</title>
  <style>
    @media print {
      body { margin: 0.75in 1in; }
      .page-break { page-break-before: always; }
    }
    body {
      font-family: 'Times New Roman', Georgia, serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #222;
      margin: 0.75in 1in;
      max-width: 7.5in;
    }
    h1 {
      text-align: center;
      font-size: 16pt;
      font-weight: bold;
      margin: 0 0 0.05in 0;
      letter-spacing: 1px;
    }
    .company-name {
      text-align: center;
      font-size: 12pt;
      font-weight: bold;
      margin: 0.05in 0 0.3in 0;
    }
    .intro {
      margin-bottom: 0.15in;
    }
    h3 {
      font-size: 11pt;
      font-weight: bold;
      margin: 0.25in 0 0.08in 0;
    }
    p {
      margin: 0.08in 0;
      text-align: justify;
    }
    ul {
      margin: 0.08in 0 0.08in 0.3in;
      padding: 0;
    }
    li {
      margin: 0.04in 0;
    }
    .signature-block {
      margin-top: 0.6in;
      page-break-inside: avoid;
    }
    .sig-row {
      display: flex;
      justify-content: space-between;
      margin-top: 0.4in;
    }
    .sig-col {
      width: 45%;
    }
    .sig-line {
      border-bottom: 1px solid #000;
      width: 100%;
      display: inline-block;
      margin-top: 0.35in;
    }
    .sig-label {
      font-size: 10pt;
      margin-top: 0.04in;
    }
  </style>
</head>
<body>

  <h1>INDEPENDENT CONTRACTOR AGREEMENT</h1>
  <p class="company-name">Roto Flight LLC</p>

  <p class="intro">This Independent Contractor Agreement (&ldquo;Agreement&rdquo;) is entered into between <strong>Roto Flight LLC</strong> (&ldquo;Company&rdquo;) and <strong>${safeName}</strong> (&ldquo;Contractor&rdquo;).</p>

  <p class="intro"><strong>Effective Date:</strong> ${formattedDate}</p>

  <p class="intro">The Company and Contractor may collectively be referred to as the &ldquo;Parties.&rdquo;</p>


  <h3>1. Scope of Services</h3>
  <p>Contractor agrees to provide professional media production and related creative services for the Company on a project-by-project basis. Services may include, but are not limited to:</p>
  <ul>
    <li>Operating production equipment and software</li>
    <li>Creative direction and technical execution</li>
    <li>Collaborating with producers, directors, and crew</li>
    <li>Camera setup, technical production workflows, and capturing video and/or still imagery</li>
    <li>Other production services reasonably requested by the Company</li>
  </ul>
  <p>The Company will provide details for each project including scope, schedule, location, and deliverables.</p>


  <h3>2. Independent Contractor Status</h3>
  <p>Contractor is engaged as an independent contractor and not an employee. Nothing in this Agreement creates an employer-employee relationship. Contractor is responsible for all federal, state, and local taxes; insurance coverage (if applicable); and any required business licenses or permits. Contractor is not eligible for employee benefits, including health insurance, workers&rsquo; compensation, or retirement plans.</p>


  <h3>3. Project Engagement</h3>
  <p>Work will be assigned on a project-by-project basis. The Company does not guarantee a minimum amount of work. Contractor is free to accept or decline assignments. Production schedules will be communicated in advance whenever possible, and Contractor agrees to notify the Company promptly if unable to attend a scheduled shoot.</p>


  <h3>4. Compensation and Expenses</h3>
  <p>${safeComp || 'Contractor will be paid according to the agreed project rate or day rate specified prior to each engagement.'} Unless otherwise agreed in writing, payment will be issued within ${esc(paymentDays)} days after invoice submission. Additional compensation may be negotiated for overtime, travel days, equipment rental, or extended shoot days.</p>
  <p>Unless otherwise agreed in writing, Contractor is responsible for their own expenses including transportation, personal equipment, meals, and lodging. Reimbursable expenses require prior approval and receipts.</p>


  <h3>5. Equipment</h3>
  <p>Contractor may use their own equipment or Company equipment depending on the project. Contractor agrees to operate all equipment professionally, use reasonable care when handling Company gear, and report any damage immediately. Contractor may be responsible for damage caused by negligence.</p>


  <h3>6. Ownership of Work Product</h3>
  <p>All video footage, photographs, audio recordings, and other media created while performing services for the Company shall be considered &ldquo;work made for hire.&rdquo; All rights, title, and interest in the content shall belong exclusively to Roto Flight LLC. Contractor agrees to assign any intellectual property rights necessary to ensure Company ownership.</p>
  <p>Contractor may use completed work for personal portfolio or promotional purposes, provided the content has already been publicly released by the Company and the use does not conflict with client confidentiality agreements.</p>


  <h3>7. Confidentiality</h3>
  <p>Contractor agrees to keep confidential any non-public information obtained during projects, including client identities, production plans, pricing structures, business strategies, and unreleased footage. This obligation survives termination of this Agreement.</p>


  ${includeClientProtection ? `<h3>${s8}. Client Protection</h3>
  <p>During the term of this Agreement and for ${esc(nonSolicitMonths)} months following the last project, Contractor agrees not to directly solicit, contact, or contract with clients introduced by the Company for competing production services without the Company&rsquo;s prior written consent. This clause does not prevent Contractor from working independently with unrelated clients.</p>` : '<!-- Client Protection omitted -->'}


  <h3>${s9label}. Safety, Compliance, and Drone Operations</h3>
  <p>Contractor agrees to follow all applicable laws, safety procedures on production sites, and maintain professional conduct while representing the production.</p>
  ${includeDroneOps ? `<p>If Contractor participates in drone operations, Contractor must hold a valid FAA Part 107 Remote Pilot Certificate if operating the drone and comply with all FAA regulations and airspace restrictions. Unless otherwise agreed, Roto Flight LLC will act as the responsible drone operator and maintain operational control.</p>` : ''}


  ${includeKillFee ? `<h3>${s10}. Kill Fee / Cancellation</h3>
  <p>If a confirmed shoot is canceled by the Company within ${esc(killFeeHours)} hours of the scheduled start time, Contractor may be entitled to a kill fee equal to ${esc(killFeePercent)}% of the agreed day rate. No kill fee applies for cancellations with more than ${esc(killFeeHours)} hours&rsquo; notice. If cancellation occurs due to weather, safety concerns, or circumstances outside the Company&rsquo;s control (force majeure), the Company may reschedule without penalty.</p>` : '<!-- Kill Fee omitted -->'}


  <h3>${s11}. Subcontracting</h3>
  <p>Contractor may not assign or subcontract services under this Agreement without written approval from the Company.</p>


  <h3>${s12}. Term and Termination</h3>
  <p>This Agreement begins on the Effective Date and continues until terminated by either party with written notice. Termination will not affect payment obligations for services already performed.</p>


  <h3>${s13}. Indemnification and Limitation of Liability</h3>
  <p>Contractor agrees to indemnify and hold harmless the Company from claims, damages, or liabilities arising from Contractor&rsquo;s negligence, violation of laws, or breach of this Agreement. The Company shall not be liable for indirect, incidental, or consequential damages. Total liability shall not exceed the amount paid to Contractor for the project giving rise to the claim.</p>


  <h3>${s14}. Governing Law</h3>
  <p>This Agreement shall be governed by the laws of the State of Wyoming, without regard to conflict of law principles.</p>


  <h3>${s15}. Entire Agreement</h3>
  <p>This Agreement represents the entire understanding between the Parties and supersedes any prior agreements. Any amendments must be made in writing and signed by both Parties.</p>


  <div class="signature-block">
    <p><strong>Roto Flight LLC</strong></p>
    <p style="margin-top: 0.35in;">By: <span class="sig-line"></span></p>
    <p class="sig-label">${esc(formData.authorizedRep || profile?.full_name || profile?.email || '')}, Authorized Representative</p>
    <p style="margin-top: 0.15in;">Date: <span class="sig-line"></span></p>

    <p style="margin-top: 0.5in;"><strong>Contractor</strong></p>
    <p style="margin-top: 0.35in;">Full Name: <span class="sig-line"></span></p>
    <p style="margin-top: 0.25in;">Signature: <span class="sig-line"></span></p>
    <p style="margin-top: 0.15in;">Date: <span class="sig-line"></span></p>
  </div>

</body>
</html>`;

    return html;
  };

  const handleGenerate = async () => {
    if (!formData.fullName.trim()) {
      toast('Full Name is required', 'error');
      return;
    }

    try {
      setIsGenerating(true);

      const html = generatePrintableHTML();
      const newWindow = window.open('', '_blank');
      newWindow.document.write(html);
      newWindow.document.close();

      await createLegalDoc.mutateAsync({
        id: uid(),
        type: 'ica',
        party: formData.fullName,
        status: 'pending',
        date: formData.date,
        notes: `ICA generated. Compensation: ${formData.compensationTerms || 'per-project'}`,
        fileName: null,
        fileData: null,
      });

      toast('ICA generated and logged');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors';
  const labelCls = 'block text-sm font-medium text-text-primary mb-1';

  return (
    <div className="card p-6 bg-surface border border-border rounded-lg">
      <h2 className="text-lg font-heading text-text-primary mb-6">Generate ICA</h2>

      <div className="space-y-4 max-w-2xl">
        {/* Contractor Name */}
        <div>
          <label className={labelCls}>
            Contractor Full Name <span className="text-red-accent">*</span>
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="John Doe"
            className={inputCls}
          />
        </div>

        {/* Authorized Representative */}
        <div>
          <label className={labelCls}>Authorized Representative</label>
          <select
            name="authorizedRep"
            value={formData.authorizedRep}
            onChange={handleChange}
            className={inputCls}
          >
            {roster.filter(r => r.name).map(r => (
              <option key={r.id} value={r.name}>{r.name}</option>
            ))}
          </select>
          <p className="text-xs text-text-dim mt-1">Person signing on behalf of Roto Flight LLC</p>
        </div>

        {/* Effective Date */}
        <div>
          <label className={labelCls}>Effective Date</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={inputCls}
          />
        </div>

        {/* Compensation */}
        <div>
          <label className={labelCls}>Compensation Terms</label>
          <input
            type="text"
            name="compensationTerms"
            value={formData.compensationTerms}
            onChange={handleChange}
            placeholder="e.g. $850/day rate, per-project basis"
            className={inputCls}
          />
          <p className="text-xs text-text-dim mt-1">Leave blank for default: &ldquo;agreed project rate or day rate specified prior to each engagement&rdquo;</p>
        </div>

        {/* Payment Days */}
        <div>
          <label className={labelCls}>Payment Terms (days after invoice)</label>
          <input
            type="number"
            name="paymentDays"
            value={formData.paymentDays}
            onChange={handleChange}
            min="1"
            max="90"
            className={inputCls}
          />
        </div>

        {/* Optional Sections */}
        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-medium text-text-primary mb-3">Optional Sections</h3>
          <div className="space-y-3">

            {/* Drone Operations */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="includeDroneOps"
                checked={formData.includeDroneOps}
                onChange={handleChange}
                className="w-4 h-4 rounded border-border"
              />
              <span className="text-sm text-text-primary">Drone Operations (FAA Part 107)</span>
            </label>

            {/* Client Protection */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="includeClientProtection"
                  checked={formData.includeClientProtection}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-border"
                />
                <span className="text-sm text-text-primary">Client Protection / Non-Solicit</span>
              </label>
              {formData.includeClientProtection && (
                <div className="ml-6 mt-2">
                  <label className="text-xs text-text-dim">Non-solicit period (months)</label>
                  <input
                    type="number"
                    name="nonSolicitMonths"
                    value={formData.nonSolicitMonths}
                    onChange={handleChange}
                    min="1"
                    max="36"
                    className={inputCls + ' mt-1'}
                  />
                </div>
              )}
            </div>

            {/* Kill Fee */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="includeKillFee"
                  checked={formData.includeKillFee}
                  onChange={handleChange}
                  className="w-4 h-4 rounded border-border"
                />
                <span className="text-sm text-text-primary">Kill Fee / Cancellation Policy</span>
              </label>
              {formData.includeKillFee && (
                <div className="ml-6 mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-text-dim">Kill fee (% of day rate)</label>
                    <input
                      type="number"
                      name="killFeePercent"
                      value={formData.killFeePercent}
                      onChange={handleChange}
                      min="1"
                      max="100"
                      className={inputCls + ' mt-1'}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-text-dim">Notice window (hours)</label>
                    <input
                      type="number"
                      name="killFeeHours"
                      value={formData.killFeeHours}
                      onChange={handleChange}
                      min="1"
                      max="168"
                      className={inputCls + ' mt-1'}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Always-included sections preview */}
        <div className="border-t border-border pt-4">
          <h3 className="text-sm font-medium text-text-dim mb-2">Always Included (15-section template)</h3>
          <ul className="text-xs text-text-dim space-y-0.5 list-disc ml-4">
            <li>Scope of Services (media production)</li>
            <li>Independent Contractor Status</li>
            <li>Project Engagement (per-project basis)</li>
            <li>Compensation and Expenses</li>
            <li>Equipment</li>
            <li>Ownership of Work Product (work-for-hire + portfolio)</li>
            <li>Confidentiality (survives termination)</li>
            <li>Safety &amp; Compliance</li>
            <li>Subcontracting (requires approval)</li>
            <li>Term and Termination</li>
            <li>Indemnification &amp; Limitation of Liability</li>
            <li>Governing Law (Wyoming)</li>
            <li>Entire Agreement</li>
          </ul>
        </div>
      </div>

      {/* Generate Button */}
      <div className="flex justify-end gap-2 mt-6">
        <button
          onClick={handleGenerate}
          disabled={isGenerating || createLegalDoc.isPending}
          className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isGenerating || createLegalDoc.isPending ? 'Generating...' : 'Generate & Print'}
        </button>
      </div>
    </div>
  );
}
